/**
 * THE WEEK, AS IT UNFOLDS.
 *
 * RACE WEEK is where the season's story is generated — the feuds, the fines,
 * the suspensions, the injuries, the interviews and the one race a season where
 * somebody does not come home. The grid is a side-effect. So this screen is a
 * newspaper of the week and not a table of numbers: the printed chart line is
 * the headline, and the dice and the chart code sit beside it in small mono
 * type like a sub-editor's note.
 *
 * WHAT IS ON SCREEN AND WHERE IT CAME FROM. Every line of chart text is
 * `RaceWeekResult`'s own verbatim string, typos and all. Every die face is the
 * face the engine rolled, lifted off the ledger event that reading emitted.
 * Every fine, feud, suspension and chip is an entry on the result. Nothing here
 * re-reads a chart, re-rolls a die or decides an outcome; if a value is not in
 * the result it is not on the screen.
 *
 * THE CHAIN IS THE DRAMA. A TV LIST reading can send a driver on to TV EVENT X,
 * and X on to Y, and Y on to Z — the escalation from "sponsor in the news" to
 * "cannot race this week" happens across two or three charts, so the whole
 * chain is drawn, each link with the dice that produced it, and the row that
 * finally spoke set on card stock.
 */

import type { FeudHistory } from '../domain/feudHistory.ts';
import { feudKeyOf, historyLine } from '../domain/feudHistory.ts';
import type { DriverCard } from '../domain/types.ts';
import { dieRoles } from './EventFeed.tsx';
import type {
  ChainStep,
  ConsequenceGroup,
  StackSummary,
  TvBeat,
  WeekStory,
} from './raceWeekModel.ts';
import { SHORT_CHART, chainLabel, isRevealed } from './raceWeekModel.ts';
import { Cite } from './Cite.tsx';
import { useCiteText } from '../lib/prefs.ts';

export interface WeekRevealProps {
  story: WeekStory;
  cards: ReadonlyMap<string, DriverCard>;
  teamNames: ReadonlyMap<string, string>;
  /** How many beats have been revealed. Everything below this index is on screen. */
  cursor: number;
  /**
   * WHAT EACH FEUDING PAIR HAS ALREADY DONE TO EACH OTHER, keyed by `feudKeyOf`.
   *
   * Optional because a single exhibition race has no season behind it and must
   * not pretend otherwise — a missing history draws no chevron at all, rather
   * than an empty one that implies these two have never met.
   */
  feudHistory?: ReadonlyMap<string, FeudHistory>;
  /** The race number this week belongs to, so "going back to" can be honest. */
  raceNumber?: number | null;
}

export default function WeekReveal({
  story,
  cards,
  teamNames,
  cursor,
  feudHistory,
  raceNumber = null,
}: WeekRevealProps) {
  const nameOf = (id: string) => cards.get(id)?.name ?? id;
  const shown = (id: string) => isRevealed(story, id, cursor);
  const tvShown = story.tv.filter((beat) => shown(beat.id));

  return (
    <div className="rwweek">
      {/* ---- STEP ONE ------------------------------------------------------ */}
      {shown('open') && story.opening && (
        <section className="rwsec rwsec--open reveal" aria-labelledby="rw-step1">
          <p className="kicker">Step one</p>
          <h2 id="rw-step1">The cards are shuffled</h2>
          <p className="rwsec__lead">{story.opening.line}</p>
          <Cite as="p" className="cite">{story.opening.citation}</Cite>
        </section>
      )}

      {/* ---- STEP TWO: the TV list ---------------------------------------- */}
      {shown('tv-count') && (
        <section className="rwsec reveal" aria-labelledby="rw-step2">
          <header className="rwsec__head">
            <p className="kicker">Step two</p>
            <h2 id="rw-step2">The TV list</h2>
            {story.tvCount && (
              <div className="rwcount">
                <DiceCluster faces={story.tvCount.dice} role="metallic" label="metallic dice" />
                <p className="rwcount__line">{story.tvCount.line}</p>
              </div>
            )}
            {story.goldFirstId && (
              <p className="rwsec__note">
                <b>{nameOf(story.goldFirstId)}</b> is first on the list and counts as a GOLD TROPHY
                driver this week, whether he has one or not.
              </p>
            )}
            {story.tvCount && <Cite as="p" className="cite">{story.tvCount.citation}</Cite>}
          </header>

          <ol className="rwclips">
            {tvShown.map((beat) => (
              <li key={beat.id} className="reveal">
                <TvClipping beat={beat} cards={cards} teamNames={teamNames} />
              </li>
            ))}
          </ol>
          {tvShown.length < story.tv.length && (
            <p className="rwsec__pending" aria-live="polite">
              {story.tv.length - tvShown.length} still to be read…
            </p>
          )}
        </section>
      )}

      {/* ---- The interview room -------------------------------------------- */}
      {shown('interviews') && story.interviews.length > 0 && (
        <section className="rwsec rwsec--quotes reveal" aria-labelledby="rw-quotes">
          <header className="rwsec__head">
            <p className="kicker">
              TV interviews<Cite> · RWEG p.7</Cite>
            </p>
            <h2 id="rw-quotes">What they said</h2>
            <p className="rwsec__sub">
              Printed exactly as the chart prints it. The bracketed words are the booklet&rsquo;s own
              substitutable tokens.
            </p>
          </header>
          <div className="rwquotes">
            {story.interviews.map((beat) => (
              <figure key={`quote-${beat.driverId}`} className="rwquote">
                {/* The chart prints its own quotation marks. Nothing is added. */}
                <blockquote>
                  <p>{beat.text}</p>
                </blockquote>
                <figcaption>
                  <span className="rwquote__who">{nameOf(beat.driverId)}</span>
                  <Cite className="rwquote__code">
                    INTERVIEW {beat.code}
                    {beat.placement ? ` · placement ${beat.placement}` : ''}
                  </Cite>
                </figcaption>
              </figure>
            ))}
          </div>
        </section>
      )}

      {/* ---- Bad weather ---------------------------------------------------- */}
      {shown('weather') && story.weather && (
        <section className="rwsec rwweather reveal" aria-labelledby="rw-weather">
          <p className="kicker kicker--alarm">Qualifying cancelled</p>
          <h2 id="rw-weather">Bad weather</h2>
          <p className="rwweather__text">{story.weather.text}</p>
          <p className="rwweather__sub">
            Every placement made this week is thrown out and the whole field takes PLACEMENT C.
            Steps three, four and five do not happen — there was no qualifying to have a fast or a
            slow week in. The fines, injuries, suspensions and feuds already generated all stand.
          </p>
          <Cite as="p" className="cite">{story.weather.citation}</Cite>
        </section>
      )}

      {/* ---- STEPS THREE, FOUR, FIVE ---------------------------------------- */}
      {story.stacks.some((stack) => shown(`stack-${stack.key}`)) && (
        <section className="rwsec reveal" aria-labelledby="rw-stacks">
          <header className="rwsec__head">
            <p className="kicker">Steps three, four and five</p>
            <h2 id="rw-stacks">How the rest of the deck was dealt</h2>
            <p className="rwsec__sub">
              No die roll decides where these men start — the metallic pair says how many had the
              week, and the trophy on the card says where each one lines up.
            </p>
          </header>
          <div className="rwstacks">
            {story.stacks
              .filter((stack) => stack.key === 'tv' || shown(`stack-${stack.key}`))
              .map((stack) => (
                <StackPanel key={stack.key} stack={stack} nameOf={nameOf} />
              ))}
          </div>
        </section>
      )}

      {/* ---- The consequences ------------------------------------------------ */}
      {story.consequences.some((group) => shown(`consequence-${group.kind}`)) && (
        <section className="rwsec reveal" aria-labelledby="rw-conseq">
          <header className="rwsec__head">
            <p className="kicker">The bill for the week</p>
            <h2 id="rw-conseq">Consequences</h2>
            <p className="rwsec__sub">
              Each one carries the driver it lands on and the chart row that caused it.
            </p>
          </header>
          <div className="rwboard">
            {story.consequences
              .filter((group) => shown(`consequence-${group.kind}`))
              .map((group) => (
                <ConsequencePanel
                  key={group.kind}
                  group={group}
                  nameOf={nameOf}
                  feudHistory={feudHistory}
                  raceNumber={raceNumber}
                />
              ))}
          </div>
        </section>
      )}
    </div>
  );
}

// ---------------------------------------------------------------------------
// One TV LIST reading
// ---------------------------------------------------------------------------

function TvClipping({
  beat,
  cards,
  teamNames,
}: {
  beat: TvBeat;
  cards: ReadonlyMap<string, DriverCard>;
  teamNames: ReadonlyMap<string, string>;
}) {
  const ct = useCiteText();
  const card = cards.get(beat.driverId);
  const team = teamNames.get(card?.teamId ?? '');
  // A partner never rolled, so his trail is the row that landed on him.
  const trail =
    beat.chain.length > 0
      ? chainLabel(beat.chain)
      : beat.chart && beat.code
        ? `${SHORT_CHART[beat.chart]} ${beat.code}`
        : null;

  return (
    <article className={`clip clip--${beat.kind}${beat.isInterview ? ' clip--quote' : ''}`}>
      <header className="clip__head">
        <h3 className="clip__who">{card?.name ?? beat.driverId}</h3>
        <p className="clip__id">
          car {card?.carNumber ?? '—'}
          {team ? ` · ${team}` : ' · independent'}
          {card?.trophy ? ` · ${card.trophy} trophy` : ''}
        </p>
        <ul className="clip__tags">
          {beat.goldThisWeek && (
            <li className="rwtag rwtag--gold" title={ct('RWEG/p.2 — first on the TV list', 'First on the TV list')}>
              gold this week
            </li>
          )}
          {beat.kind === 'carry-over' && <li className="rwtag rwtag--tv">no roll · carried over</li>}
          {beat.kind === 'partner' && <li className="rwtag rwtag--feud">drawn in</li>}
          {beat.isInterview && <li className="rwtag rwtag--quiet">interview</li>}
        </ul>
        {trail && <p className="clip__trail">{trail}</p>}
      </header>

      {beat.kind === 'roll' ? (
        <ol className="clip__chain">
          {beat.chain.map((step, i) => (
            <ChainLink key={`${step.chart}-${step.code}-${i}`} step={step} quote={beat.isInterview} />
          ))}
        </ol>
      ) : (
        <p className={`clip__printed${beat.kind === 'carry-over' ? ' clip__printed--quiet' : ''}`}>
          {beat.kind === 'partner' && <span className="clip__lead">Drawn in as the other half of it — </span>}
          {beat.text}
        </p>
      )}

      {beat.extra.length > 0 && (
        <p className="clip__extra">
          {beat.extra.map((roll, i) => (
            <span key={i} className="clip__extraroll">
              <span className="clip__extralabel">{roll.label}</span>
              <DiceCluster faces={roll.dice} role="black" label={roll.label ?? 'roll'} />
              {roll.total !== undefined && <b>= {roll.total}</b>}
            </span>
          ))}
        </p>
      )}

      <footer className="clip__foot">
        <span className={`clip__place${beat.placement ? '' : ' clip__place--none'}`}>
          {beat.placement ? `Placement ${beat.placement}` : 'No placement letter'}
          {beat.kind === 'roll' && <> · blue die {beat.blue}</>}
        </span>
        {/* Where he actually ended up, in the engine's own sentence. */}
        {beat.outcome && <span className="clip__outcome">{beat.outcome.line}</span>}
        <Cite className="cite">{beat.citation}</Cite>
      </footer>
    </article>
  );
}

/** One link in the chain: the code, the throw that made it, and the printed row. */
function ChainLink({ step, quote }: { step: ChainStep; quote: boolean }) {
  return (
    <li className={`link${step.last ? ' link--final' : ''}`}>
      <div className="link__read">
        <span className="link__chart">{step.chartName}</span>
        <span className="link__code">{step.code}</span>
        {step.read && <DiceCluster faces={step.read.dice} roles={dieRoles(step.read)} label="red and white" />}
        {step.blue && <DiceCluster faces={step.blue.dice} role="blue" label="blue die" />}
      </div>
      {step.last && quote ? (
        // The TV INTERVIEWS chart prints its own quotation marks; adding a
        // second pair would be editing the card.
        <blockquote className="link__text link__text--quote">
          <p>{step.text}</p>
        </blockquote>
      ) : (
        <p className={`link__text${step.last ? ' link__text--printed' : ''}`}>{step.text}</p>
      )}
    </li>
  );
}

// ---------------------------------------------------------------------------
// Stacks and consequences
// ---------------------------------------------------------------------------

function StackPanel({ stack, nameOf }: { stack: StackSummary; nameOf: (id: string) => string }) {
  return (
    <section className={`rwstack rwstack--${stack.key}`} aria-labelledby={`stack-${stack.key}`}>
      <header className="rwstack__head">
        <span className="rwstack__step">Step {stack.step}</span>
        <h3 id={`stack-${stack.key}`}>{stack.title}</h3>
        <span className="rwstack__n">
          {stack.driverIds.length} <i>driver{stack.driverIds.length === 1 ? '' : 's'}</i>
        </span>
      </header>
      {stack.count && stack.count.dice.length > 0 && (
        <p className="rwstack__roll">
          <DiceCluster faces={stack.count.dice} role="metallic" label="metallic dice" />
          <span className="rwstack__total">= {stack.count.total}</span>
        </p>
      )}
      <p className="rwstack__rule">{stack.rule}</p>
      {stack.driverIds.length > 0 ? (
        // A scroll region needs a name and a way in from the keyboard.
        <ul
          className="rwstack__names"
          tabIndex={0}
          aria-label={`${stack.title} — the drivers dealt into this stack`}
        >
          {stack.driverIds.map((id) => (
            <li key={id}>{nameOf(id)}</li>
          ))}
        </ul>
      ) : (
        <p className="rwstack__none">Nobody was dealt into this stack.</p>
      )}
      {stack.count && <Cite as="p" className="cite">{stack.count.citation}</Cite>}
    </section>
  );
}

function ConsequencePanel({
  group,
  nameOf,
  feudHistory,
  raceNumber = null,
}: {
  group: ConsequenceGroup;
  nameOf: (id: string) => string;
  feudHistory?: ReadonlyMap<string, FeudHistory>;
  raceNumber?: number | null;
}) {
  return (
    <section className={`rwcon rwcon--${group.kind}`} aria-labelledby={`con-${group.kind}`}>
      <header className="rwcon__head">
        <h3 id={`con-${group.kind}`}>{group.title}</h3>
        <span className="rwcon__n">{group.rows.length}</span>
      </header>
      <p className="rwcon__note">{group.note}</p>
      <ul className="rwcon__rows">
        {group.rows.map((row) => {
          /*
            ⚠ ONLY WHERE THERE ARE TWO NAMES. The key is built from a pair, so a
            fine or an injury — one man, one row — never looks one up and never
            draws a chevron. This is not a guard against a crash; it is the
            reason the control only appears where it means something.
          */
          const history =
            group.kind === 'feud' && row.driverIds.length === 2
              ? feudHistory?.get(feudKeyOf(row.driverIds[0], row.driverIds[1]))
              : undefined;
          return (
            <li key={row.id}>
              <p className="rwcon__who">{row.driverIds.map(nameOf).join('  v  ')}</p>
              <p className="rwcon__what">{row.headline}</p>
              <p className="rwcon__detail">{row.detail}</p>
              <Cite as="p" className="cite">
                {row.chart && row.code ? <b className="rwcon__code">{row.code}</b> : null}
                {row.citation}
              </Cite>
              {history && history.total > 0 && (
                <FeudLog history={history} nameOf={nameOf} raceNumber={raceNumber} />
              )}
            </li>
          );
        })}
      </ul>
    </section>
  );
}

// ---------------------------------------------------------------------------
// The chevron: everything these two have already done to each other
// ---------------------------------------------------------------------------

/**
 * Howard, 18 Aug 2026: *"track the interactions on the track and off for feuds
 * here before the race, maybe behind a drop down chevron."*
 *
 * CLOSED BY DEFAULT, AND THE SUMMARY EARNS THE CLICK. A chevron that says only
 * "history" is a shape; this one carries the count and the split, so a player
 * scanning four feuds can tell the one with nine run-ins since April from the
 * one that started an hour ago without opening either.
 *
 * ⚠ `<details>` ON PURPOSE, not a `useState` toggle. It opens without React,
 * survives a re-render mid-reveal, is keyboard-operable and screen-reader
 * announced for free, and Ctrl-F finds the text inside it in browsers that
 * search collapsed content. A hand-rolled disclosure gets none of that right.
 */
function FeudLog({
  history,
  nameOf,
  raceNumber,
}: {
  history: FeudHistory;
  nameOf: (id: string) => string;
  raceNumber: number | null;
}) {
  const line = historyLine(history, raceNumber);
  const hidden = history.total - history.beats.length;
  /*
    ⚠ NOT WHEN IT IS ALREADY THE FIRST ROW. On a grudge that started this week
    the origin IS the top of the list, and printing it twice made the panel
    look like it had a bug in it. It earns its box only when the thing that
    started it has scrolled out of the top of the story.
  */
  const origin = history.origin && history.origin.id !== history.beats[0]?.id ? history.origin : null;
  return (
    <details className="feudlog">
      <summary className="feudlog__toggle">
        <span className="feudlog__chev" aria-hidden="true" />
        <span className="feudlog__line">{line}</span>
      </summary>
      {origin && (
        <p className="feudlog__origin">
          <span className="feudlog__originlabel">What started it</span>
          {origin.text}
        </p>
      )}
      {hidden > 0 && (
        <p className="feudlog__trimmed">
          The {hidden} oldest {hidden === 1 ? 'run-in is' : 'run-ins are'} not printed here. The
          season record still has {hidden === 1 ? 'it' : 'them'}.
        </p>
      )}
      <ol className="feudlog__beats">
        {history.beats.map((beat) => (
          <li key={beat.id} className={`feudbeat feudbeat--${beat.where} feudbeat--${beat.weight}`}>
            <p className="feudbeat__when">
              {beat.raceName ?? (beat.raceNumber !== null ? `Race ${beat.raceNumber}` : 'This week')}
              {beat.turn !== null && <> · turn {beat.turn}</>}
              <span className="feudbeat__where">
                {beat.where === 'track' ? 'on the racetrack' : 'off the racetrack'}
              </span>
            </p>
            <p className="feudbeat__label">{beat.label}</p>
            {/* D-003 — the ledger's own sentence, not a paraphrase of it. */}
            <p className="feudbeat__text">{beat.text}</p>
            <p className="cite">
              {beat.dice.map((roll, i) => (
                <DiceCluster
                  key={i}
                  faces={roll.dice}
                  roles={dieRoles(roll)}
                  label={roll.label ?? beat.label}
                />
              ))}
              <Cite>{beat.citation}</Cite>
            </p>
            {beat.weight === 'shared' && (
              <p className="feudbeat__shared">
                Both of them were in it. Nothing says either one meant it.
              </p>
            )}
          </li>
        ))}
      </ol>
      <p className="feudlog__foot">
        Straight from the season record — every line above is an event {nameOf(history.a)} and{' '}
        {nameOf(history.b)} were both in.
      </p>
    </details>
  );
}

// ---------------------------------------------------------------------------
// Dice
// ---------------------------------------------------------------------------

export function DiceCluster({
  faces,
  role = 'metallic',
  roles,
  label,
}: {
  faces: readonly number[];
  role?: string;
  roles?: readonly string[];
  label: string;
}) {
  return (
    <span className="dicecluster" aria-label={`${label}: ${faces.join(', ')}`}>
      {faces.map((face, i) => (
        <span key={i} className={`die die--${roles?.[i] ?? role}`} aria-hidden="true">
          {face}
        </span>
      ))}
    </span>
  );
}
